import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  QuestionListResponseProps,
  StudentProps,
  StudentListResponseProps,
  QuestionProps,
} from "@src/components/task-room-details/data.types";
import { StudentListContent } from "@src/pages/batches/batches-quiz-details/BatchesQuizDetailsTypes";

interface InitialState {
  quizData: QuestionListResponseProps | null;
  selectedQuestion: QuestionProps | null;
  studentList: StudentListResponseProps | null;
  selectedStudent: StudentProps | null;
  trainerCode: {
    [batchId: string]: {
      questionId: string;
      code: Record<string, string>;
    }[];
  };
  completedStudents: string[];
  inProgressStudents: string[];
  isTaskRoomOpen: boolean;
  isClassStarted: boolean;
  studentAnswerResponse: {
    questionId: string;
    studentId: string;
    marks: number;
    totalScore: number;
    remarks: string;
    userCode: string;
    answer: string;
  } | null;
  isAssignQuestionDrawerOpen: boolean;
  isElpQuestionsDrawerOpen: boolean;
}

const initialState: InitialState = {
  quizData: null,
  selectedQuestion: null,
  studentList: null,
  selectedStudent: null,
  trainerCode: {},
  completedStudents: [],
  inProgressStudents: [],
  isTaskRoomOpen: true,
  isClassStarted: false,
  studentAnswerResponse: null,
  isAssignQuestionDrawerOpen: true,
  isElpQuestionsDrawerOpen: true,
};

const trainerTaskRoomSlice = createSlice({
  name: "trainerTaskRoom",
  initialState,
  reducers: {
    setQuizData: (state, action: PayloadAction<QuestionListResponseProps>) => {
      state.quizData = action.payload;
      state.selectedQuestion = action.payload?.content[0];
    },

    setSelectedQuestion: (state, action: PayloadAction<QuestionProps>) => {
      const selectedQuestionIndex = state.quizData?.content?.findIndex(
        (q) => q.id === action.payload?.id
      );
      if (selectedQuestionIndex === undefined || selectedQuestionIndex === -1)
        return;

      const selectedQuestion = state.quizData?.content?.[selectedQuestionIndex];

      state.selectedQuestion = selectedQuestion ?? null;
    },

    setStudentList: (state, action) => {
      state.studentList = action.payload;
      if (!state.selectedStudent)
        state.selectedStudent = action.payload.content[0];
    },

    setSelectedStudent: (
      state,
      action: PayloadAction<StudentListContent | null>
    ) => {
      if (!state.studentList?.content) return;
      if (action.payload === null) {
        state.selectedStudent = null;
        return;
      }
      const student = state.studentList?.content?.find(
        (st) => st.studentId === action.payload?.studentId
      );
      if (student) {
        state.selectedStudent = student;
      }
    },
    setTrainerCode: (
      state,
      action: PayloadAction<{
        batchId: string;
        trainerCode: { questionId: string; code: { [key: string]: string } };
      }>
    ) => {
      const { batchId } = action.payload;

      if (!state.trainerCode) {
        state.trainerCode = {};
      }

      if (!state.trainerCode[batchId]) {
        state.trainerCode[batchId] = [];
        state.trainerCode[batchId].push(action.payload.trainerCode);
      } else {
        const index = state.trainerCode[batchId].findIndex(
          (data) => data.questionId === action.payload.trainerCode.questionId
        );
        if (index !== -1) {
          state.trainerCode[batchId].splice(
            index,
            1,
            action.payload.trainerCode
          );
        } else {
          state.trainerCode[batchId].push(action.payload.trainerCode);
        }
      }
    },
    setStudentStatusData: (state, action) => {
      state.completedStudents = action.payload.students?.completedStudents;
      state.inProgressStudents = action.payload.students?.inProgressStudents;
    },
    setIsTaskRoomOpen: (state, action: PayloadAction<boolean>) => {
      state.isTaskRoomOpen = action.payload;
    },
    setIsClassStarted: (state, action: PayloadAction<boolean>) => {
      state.isClassStarted = action.payload;
    },
    setStudentAnswerResponse: (state, action) => {
      state.studentAnswerResponse = action.payload;
    },
    setIsAssignQuestionDrawerOpen: (state, action) => {
      state.isAssignQuestionDrawerOpen = action.payload;
    },
    setIsElpQuestionsDrawerOpen: (state, action) => {
      state.isElpQuestionsDrawerOpen = action.payload;
    },
  },
});

export const {
  setQuizData,
  setSelectedQuestion,
  setStudentList,
  setSelectedStudent,
  setTrainerCode,
  setStudentStatusData,
  setIsTaskRoomOpen,
  setIsClassStarted,
  setStudentAnswerResponse,
  setIsAssignQuestionDrawerOpen,
  setIsElpQuestionsDrawerOpen,
} = trainerTaskRoomSlice.actions;
export default trainerTaskRoomSlice.reducer;
